/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React from 'react';
import { Inbox, LayoutList, UserRound, Settings, LogOut } from 'lucide-react';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import { ensureCurrentUser } from '../../../../util/data';
import { useLocale } from '../../../../context/localeContext';

import {
  AvatarLarge,
  ExternalLink,
  InlineTextButton,
  NamedLink,
  NotificationBadge,
} from '../../../../components';

import css from './TopbarMobileMenu.module.css';

const SUPPORTED_LANGUAGES = [
  { code: 'de', flag: '\u{1F1E9}\u{1F1EA}', labelKey: 'LanguageSwitcher.de' },
  { code: 'en', flag: '\u{1F1EC}\u{1F1E7}', labelKey: 'LanguageSwitcher.en' },
  { code: 'es', flag: '\u{1F1EA}\u{1F1F8}', labelKey: 'LanguageSwitcher.es' },
  { code: 'fr', flag: '\u{1F1EB}\u{1F1F7}', labelKey: 'LanguageSwitcher.fr' },
];

const MobileLanguageSwitcher = () => {
  const { locale, setLocale } = useLocale();
  const intl = useIntl();

  return (
    <div className={css.languageSwitcher}>
      <span className={css.languageSwitcherLabel}>
        <FormattedMessage id="LanguageSwitcher.label" />
      </span>
      <div className={css.languageOptions}>
        {SUPPORTED_LANGUAGES.map(lang => (
          <InlineTextButton
            key={lang.code}
            rootClassName={classNames(css.languageOption, {
              [css.languageOptionActive]: lang.code === locale,
            })}
            onClick={() => setLocale(lang.code)}
            aria-pressed={lang.code === locale}
          >
            <span className={css.languageFlag}>{lang.flag}</span>
            <span className={css.languageName}>{intl.formatMessage({ id: lang.labelKey })}</span>
          </InlineTextButton>
        ))}
      </div>
    </div>
  );
};

const CustomLinkComponent = ({ linkConfig, currentPage }) => {
  const { group, text, type, href, route } = linkConfig;
  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab => hasPageName('InboxPage') && currentPage === `${page}:${tab}`;
    const isCurrentPage = currentPage === page;

    return isCMSPage(route?.params?.pageId) || isInboxPage(route?.params?.tab) || isCurrentPage
      ? css.currentPage
      : null;
  };

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    const className = classNames(css.navigationLink, getCurrentPageClass(name));
    return (
      <li className={className}>
        <NamedLink name={name} params={params} to={to}>
          <span className={css.menuItemBorder} />
          {text}
        </NamedLink>
      </li>
    );
  }
  return (
    <li className={css.navigationLink}>
      <ExternalLink href={href}>
        <span className={css.menuItemBorder} />
        {text}
      </ExternalLink>
    </li>
  );
};

/**
 * Menu for mobile layout (opens through hamburger icon)
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isAuthenticated
 * @param {string?} props.currentPage
 * @param {boolean} props.currentUserHasListings
 * @param {Object?} props.currentUser API entity
 * @param {number} props.notificationCount
 * @param {Array<Object>} props.customLinks Contains object like { group, text, type, href, route }
 * @param {Function} props.onLogout
 * @returns {JSX.Element} search icon
 */
const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    inboxTab,
    currentUser,
    notificationCount = 0,
    customLinks,
    onLogout,
    showCreateListingsLink,
    onOpenLoginModal,
  } = props;

  const user = ensureCurrentUser(currentUser);

  const extraLinks = customLinks.map((linkConfig, index) => {
    return (
      <CustomLinkComponent
        key={`${linkConfig.text}_${index}`}
        linkConfig={linkConfig}
        currentPage={currentPage}
      />
    );
  });

  // Ein Icon je Zeile, damit das Menue scanbar ist statt gelesen werden zu muss. Nur dort, wo das
  // Icon wirklich etwas bedeutet: die redaktionellen `customLinks` aus der Console bekommen keins,
  // weil ein generisches Glyph vor beliebigem Text nur Rauschen ist.
  const withIcon = (Icon, node) => (
    <>
      <span className={css.menuIcon} aria-hidden="true">
        <Icon size={18} strokeWidth={1.5} />
      </span>
      {node}
    </>
  );

  // Guests get the space-owner landing page (pitch, calculator, FAQ before the
  // account wall); providers keep the direct path into the listing wizard.
  const createListingsLinkMaybe = showCreateListingsLink ? (
    isAuthenticated ? (
      <NamedLink className={css.createNewListingLink} name="NewListingPage">
        <FormattedMessage id="TopbarMobileMenu.listForFreeLink" />
      </NamedLink>
    ) : (
      <NamedLink className={css.createNewListingLink} name="SpaceOwnerLandingPage">
        <FormattedMessage id="TopbarMobileMenu.listSpaceLink" />
      </NamedLink>
    )
  ) : null;

  if (!isAuthenticated) {
    // One combined entry instead of a greeting sentence with two links buried inside it.
    // A menu row is a target you can hit; "möchtest du dich Registrieren oder Anmelden?" put the
    // two real targets inside running prose, which is both hard to tap and hard to scan.
    return (
      <nav className={css.root}>
        <div className={classNames(css.content, css.contentGuest)}>
          <InlineTextButton rootClassName={css.authPrimaryLink} onClick={onOpenLoginModal}>
            {withIcon(UserRound, <FormattedMessage id="TopbarDesktop.authCombined" />)}
          </InlineTextButton>

          <ul className={css.customLinksWrapper}>{extraLinks}</ul>

          <MobileLanguageSwitcher />
          <div className={css.spacer} />
        </div>
        <div className={css.footer}>{createListingsLinkMaybe}</div>
      </nav>
    );
  }

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const displayName = user.attributes.profile.firstName;
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettingsPage || isInboxPage ? css.currentPage : null;
  };

  const manageListingsLinkMaybe = showCreateListingsLink ? (
    <li className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}>
      <NamedLink name="ManageListingsPage">
        {withIcon(LayoutList, <FormattedMessage id="TopbarMobileMenu.myListingsLink" />)}
      </NamedLink>
    </li>
  ) : null;

  return (
    <div className={css.root}>
      <AvatarLarge className={css.avatar} user={currentUser} />
      <div className={css.content}>
        <span className={css.greeting}>
          <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
        </span>
        <ul className={css.accountLinksWrapper}>
          <li className={classNames(css.inbox, currentPageClass(`InboxPage:${inboxTab}`))}>
            <NamedLink name="InboxPage" params={{ tab: inboxTab }}>
              {withIcon(Inbox, <FormattedMessage id="TopbarMobileMenu.inboxLink" />)}
              {notificationCountBadge}
            </NamedLink>
          </li>
          {manageListingsLinkMaybe}
          <li className={classNames(css.navigationLink, currentPageClass('ProfileSettingsPage'))}>
            <NamedLink name="ProfileSettingsPage">
              {withIcon(UserRound, <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />)}
            </NamedLink>
          </li>
          <li className={classNames(css.navigationLink, currentPageClass('AccountSettingsPage'))}>
            <NamedLink name="AccountSettingsPage">
              {withIcon(Settings, <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />)}
            </NamedLink>
          </li>
        </ul>
        <ul className={css.customLinksWrapper}>{extraLinks}</ul>
        <MobileLanguageSwitcher />
        <div className={css.spacer} />
        {/* Abmelden steht unten, nicht oben. Bisher war es die zweite Zeile des Menues, direkt
            unter der Begruessung und ueber dem Posteingang — die prominenteste Stelle gehoerte
            damit dem Ausgang. */}
        <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
          {withIcon(LogOut, <FormattedMessage id="TopbarMobileMenu.logoutLink" />)}
        </InlineTextButton>
      </div>
      <div className={css.footer}>{createListingsLinkMaybe}</div>
    </div>
  );
};

export default TopbarMobileMenu;
